import { z } from 'zod';
import { amountCents, isoDate, uuid } from './common.js';

/**
 * GET /me/orders — the participant's purchases for the profile (P-10, P-19): one entry per
 * paid or refunded order, with its races, amount, receipt and insurance status.
 */
export const profileOrder = z.object({
  id: uuid,
  status: z.enum(['paid', 'refunded']),
  /** Order date: when it was paid. */
  paidAt: isoDate,
  /** Amount charged: formats − voucher + insurance. */
  totalCents: amountCents,
  insured: z.boolean(),
  insuranceCents: amountCents,
  /**
   * Insured order (P-19, decision 0025): where the insurance stands with the insurer and the
   * insurer's page to declare a cancellation, while a claim can still be made. Null when the
   * order isn't insured.
   */
  insurance: z
    .object({
      status: z.enum(['pending', 'declared', 'retracted', 'claimed', 'indemnified']),
      claimUrl: z.string().url().nullable(),
    })
    .nullable(),
  refundedAmountCents: amountCents,
  /** Stripe receipt; null for a free order (no payment) or while Stripe can't be reached. */
  receiptUrl: z.string().url().nullable(),
  registrations: z.array(
    z.object({
      id: uuid,
      formatId: uuid,
      formatName: z.string(),
      formatNameEn: z.string().nullable(),
      formatStartsAt: isoDate.nullable(),
      priceCents: amountCents,
      /** Team format (P-20, P-21): the team this registration belongs to, and whether the payer captains it. */
      team: z.object({ id: uuid, name: z.string(), isCaptain: z.boolean() }).nullable(),
    }),
  ),
});
export type ProfileOrder = z.infer<typeof profileOrder>;

export const profileOrders = z.object({ orders: z.array(profileOrder) });
export type ProfileOrders = z.infer<typeof profileOrders>;

/** GET /me/available-formats — formats of the event not bought yet (P-13 upsell). */
export const availableFormats = z.object({ formatIds: z.array(uuid) });
export type AvailableFormats = z.infer<typeof availableFormats>;
